import { useMutation, useQueryClient } from "@tanstack/react-query";
import ApiHttp from "byzantine/src/ApiHttp";

import { TRANSACTION_DETAIL_QUERY_KEY } from "../constants";

import { TRANSACTIONS_LIST_QUERY_KEY } from "./useTransactionsList";

import type { TransactionsListResponse } from "./useTransactionsList";

export interface UpdateTagsInput {
  transactionId: string;
  /** The whole list. The endpoint replaces the array rather than patching it. */
  tags: string[];
}

export interface UseUpdateTransactionTagsOptions {
  onError?: () => void;
}

// Order counts, so this is not a set comparison. Joining on a delimiter would match two different
// lists whenever a tag contains that delimiter.
const sameTags = (a: string[] | undefined, b: string[]): boolean =>
  a !== undefined &&
  a.length === b.length &&
  a.every((tag, index) => tag === b[index]);

const applyTags = (
  response: TransactionsListResponse | undefined,
  transactionId: string,
  tags: string[]
): TransactionsListResponse | undefined => {
  if (!response) return response;
  return {
    ...response,
    transactions: response.transactions.map((transaction) =>
      transaction.id === transactionId ? { ...transaction, tags } : transaction
    ),
  };
};

const restoreTags = (
  response: TransactionsListResponse | undefined,
  previous: API.Transaction,
  optimisticTags: string[]
): TransactionsListResponse | undefined => {
  if (!response) return response;
  return {
    ...response,
    transactions: response.transactions.map((transaction) =>
      transaction.id === previous.id &&
      sameTags(transaction.tags, optimisticTags)
        ? { ...transaction, tags: previous.tags }
        : transaction
    ),
  };
};

export const useUpdateTransactionTags = ({
  onError,
}: UseUpdateTransactionTagsOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ transactionId, tags }: UpdateTagsInput) =>
      // The body is the third positional argument, not an option.
      ApiHttp.fetch(
        `transactions/${transactionId}`,
        { method: "PUT" },
        { tags }
      ),

    onMutate: async ({ transactionId, tags }: UpdateTagsInput) => {
      await queryClient.cancelQueries({
        queryKey: [TRANSACTIONS_LIST_QUERY_KEY],
      });
      await queryClient.cancelQueries({
        queryKey: [TRANSACTION_DETAIL_QUERY_KEY, transactionId],
      });

      const previousRows = queryClient
        .getQueriesData<TransactionsListResponse>({
          queryKey: [TRANSACTIONS_LIST_QUERY_KEY],
        })
        .flatMap(([key, response]) => {
          const row = response?.transactions.find(
            (transaction) => transaction.id === transactionId
          );
          return row ? [[key, row] as const] : [];
        });

      queryClient.setQueriesData<TransactionsListResponse>(
        { queryKey: [TRANSACTIONS_LIST_QUERY_KEY] },
        (response) => applyTags(response, transactionId, tags)
      );

      return { previousRows };
    },

    onError: (_error, { tags }: UpdateTagsInput, context) => {
      context?.previousRows.forEach(([key, row]) => {
        queryClient.setQueryData<TransactionsListResponse>(key, (response) =>
          restoreTags(response, row, tags)
        );
      });
      onError?.();
    },

    // Query-managed, not a side fetch: cancelQueries above cancels a stale one from an earlier
    // settle, and returning the promise keeps isPending true until the panel is actually fresh.
    onSettled: (_data, _error, { transactionId }: UpdateTagsInput) =>
      queryClient.invalidateQueries({
        queryKey: [TRANSACTION_DETAIL_QUERY_KEY, transactionId],
      }),
  });
};
